import { useTranslation } from 'react-i18next';
import {
  Code, DocSection, LegalShell, P, Strong, UL,
} from '@/components/public/LegalShell';

const LAST_UPDATED = '2026-06-03';

/* ────────────────────────────────────────────────────────────────
   Security
   ──────────────────────────────────────────────────────────────── */
export function SecurityPage() {
  const { t } = useTranslation();
  const toc = [
    { id: 'encryption',     label: t('security.toc.encryption') },
    { id: 'access',         label: t('security.toc.access') },
    { id: 'audit',          label: t('security.toc.audit') },
    { id: 'network',        label: t('security.toc.network') },
    { id: 'data-residency', label: t('security.toc.residency') },
    { id: 'pii',            label: t('security.toc.pii') },
    { id: 'reporting',      label: t('security.toc.reporting') },
    { id: 'vulnerability',  label: t('security.toc.disclosure') },
  ];
  return (
    <LegalShell
      eyebrow={t('security.eyebrow')}
      title={t('security.title')}
      subtitle={t('security.subtitle')}
      lastUpdated={LAST_UPDATED}
      toc={toc}
    >
      <DocSection id="encryption" title={t('security.encryption.title')}>
        <P>{t('security.encryption.p1')}</P>
        <UL>
          <li>{t('security.encryption.l1')}</li>
          <li>{t('security.encryption.l2')}</li>
          <li>{t('security.encryption.l3')}</li>
        </UL>
      </DocSection>

      <DocSection id="access" title={t('security.access.title')}>
        <P>{t('security.access.p1')}</P>
        <UL>
          <li>{t('security.access.l1')}</li>
          <li>{t('security.access.l2')}</li>
          <li>{t('security.access.l3')}</li>
        </UL>
      </DocSection>

      <DocSection id="audit" title={t('security.audit.title')}>
        <P>{t('security.audit.p1')}</P>
        <P>{t('security.audit.p2')}</P>
      </DocSection>

      <DocSection id="network" title={t('security.network.title')}>
        <UL>
          <li>{t('security.network.l1')}</li>
          <li>{t('security.network.l2')}</li>
          <li>{t('security.network.l3')}</li>
          <li>{t('security.network.l4')}</li>
        </UL>
      </DocSection>

      <DocSection id="data-residency" title={t('security.residency.title')}>
        <P>{t('security.residency.p1')}</P>
        <UL>
          <li><Strong>EU (Frankfurt)</Strong> · {t('security.residency.eu')}</li>
          <li><Strong>JP (Tokyo)</Strong> · {t('security.residency.jp')}</li>
        </UL>
      </DocSection>

      <DocSection id="pii" title={t('security.pii.title')}>
        <P>{t('security.pii.p1')}</P>
        <UL>
          <li>{t('security.pii.l1')}</li>
          <li>{t('security.pii.l2')}</li>
          <li>{t('security.pii.l3')}</li>
        </UL>
      </DocSection>

      <DocSection id="reporting" title={t('security.reporting.title')}>
        <P>{t('security.reporting.p1')}</P>
      </DocSection>

      <DocSection id="vulnerability" title={t('security.disclosure.title')}>
        <P>{t('security.disclosure.p1')}</P>
        <P>
          {t('security.disclosure.contact_prefix')} <Code>security@keenez.com</Code>
        </P>
      </DocSection>
    </LegalShell>
  );
}

/* ────────────────────────────────────────────────────────────────
   Status
   ──────────────────────────────────────────────────────────────── */
export function StatusPage() {
  const { t } = useTranslation();
  const systems: Array<{ name: string; state: 'ok' | 'degraded' | 'down' }> = [
    { name: 'API', state: 'ok' },
    { name: 'Web', state: 'ok' },
    { name: 'AI agents', state: 'ok' },
    { name: 'JP payroll engine', state: 'ok' },
    { name: 'US / UK / DE payroll engines', state: 'ok' },
    { name: 'E-sign + PDF generation', state: 'ok' },
    { name: 'Background workers', state: 'ok' },
  ];
  return (
    <LegalShell
      eyebrow={t('status.eyebrow')}
      title={t('status.title')}
      subtitle={t('status.subtitle')}
      lastUpdated={LAST_UPDATED}
    >
      <ul className="space-y-3 mt-6">
        {systems.map((s) => (
          <li key={s.name} className="flex items-center justify-between rounded-2xl border border-border bg-card/80 px-4 py-3">
            <span className="text-sm font-medium">{s.name}</span>
            <span className="inline-flex items-center gap-2 text-xs font-semibold">
              <span className={`h-2 w-2 rounded-full ${s.state === 'ok' ? 'bg-emerald-500' : s.state === 'degraded' ? 'bg-amber-500' : 'bg-rose-500'}`} />
              {s.state === 'ok' ? t('status.operational') : s.state === 'degraded' ? t('status.degraded') : t('status.down')}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-6 text-xs text-muted-foreground">{t('status.disclaimer')}</p>
    </LegalShell>
  );
}

/* ────────────────────────────────────────────────────────────────
   Changelog
   ──────────────────────────────────────────────────────────────── */
export function ChangelogPage() {
  const { t } = useTranslation();
  return (
    <LegalShell
      eyebrow={t('changelog.eyebrow')}
      title={t('changelog.title')}
      subtitle={t('changelog.subtitle')}
      lastUpdated={LAST_UPDATED}
    >
      <DocSection id="v1-2026-05" title={t('changelog.v1.title')}>
        <P>{t('changelog.v1.body')}</P>
        <UL>
          <li>{t('changelog.v1.l1')}</li>
          <li>{t('changelog.v1.l2')}</li>
          <li>{t('changelog.v1.l3')}</li>
          <li>{t('changelog.v1.l4')}</li>
          <li>{t('changelog.v1.l5')}</li>
          <li>{t('changelog.v1.l6')}</li>
          <li>{t('changelog.v1.l7')}</li>
        </UL>
      </DocSection>
    </LegalShell>
  );
}

/* ────────────────────────────────────────────────────────────────
   Terms of Service
   ──────────────────────────────────────────────────────────────── */
export function TermsPage() {
  const { t } = useTranslation();
  const toc = [
    { id: 'who',         label: t('terms.toc.who') },
    { id: 'account',     label: t('terms.toc.account') },
    { id: 'acceptable',  label: t('terms.toc.acceptable') },
    { id: 'fees',        label: t('terms.toc.fees') },
    { id: 'data',        label: t('terms.toc.data') },
    { id: 'ip',          label: t('terms.toc.ip') },
    { id: 'warranties',  label: t('terms.toc.warranties') },
    { id: 'liability',   label: t('terms.toc.liability') },
    { id: 'termination', label: t('terms.toc.termination') },
    { id: 'law',         label: t('terms.toc.law') },
    { id: 'changes',     label: t('terms.toc.changes') },
  ];
  return (
    <LegalShell
      eyebrow={t('terms.eyebrow')}
      title={t('terms.title')}
      subtitle={t('terms.subtitle')}
      lastUpdated={LAST_UPDATED}
      toc={toc}
    >
      <DocSection id="who" title={t('terms.who.title')}>
        <P>{t('terms.who.p1')}</P>
        <P>{t('terms.who.p2')}</P>
      </DocSection>

      <DocSection id="account" title={t('terms.account.title')}>
        <P>{t('terms.account.p1')}</P>
        <UL>
          <li>{t('terms.account.l1')}</li>
          <li>{t('terms.account.l2')}</li>
          <li>{t('terms.account.l3')}</li>
        </UL>
      </DocSection>

      <DocSection id="acceptable" title={t('terms.acceptable.title')}>
        <P>{t('terms.acceptable.p1')}</P>
      </DocSection>

      <DocSection id="fees" title={t('terms.fees.title')}>
        <P>{t('terms.fees.p1')}</P>
        <UL>
          <li>{t('terms.fees.l1')}</li>
          <li>{t('terms.fees.l2')}</li>
          <li>{t('terms.fees.l3')}</li>
        </UL>
      </DocSection>

      <DocSection id="data" title={t('terms.data.title')}>
        <P>{t('terms.data.p1')}</P>
        <P>{t('terms.data.p2')}</P>
      </DocSection>

      <DocSection id="ip" title={t('terms.ip.title')}>
        <P>{t('terms.ip.p1')}</P>
      </DocSection>

      <DocSection id="warranties" title={t('terms.warranties.title')}>
        <P>{t('terms.warranties.p1')}</P>
      </DocSection>

      <DocSection id="liability" title={t('terms.liability.title')}>
        <P>{t('terms.liability.p1')}</P>
      </DocSection>

      <DocSection id="termination" title={t('terms.termination.title')}>
        <P>{t('terms.termination.p1')}</P>
      </DocSection>

      <DocSection id="law" title={t('terms.law.title')}>
        <P>{t('terms.law.p1')}</P>
      </DocSection>

      <DocSection id="changes" title={t('terms.changes.title')}>
        <P>{t('terms.changes.p1')}</P>
      </DocSection>
    </LegalShell>
  );
}

/* ────────────────────────────────────────────────────────────────
   Privacy Policy
   ──────────────────────────────────────────────────────────────── */
export function PrivacyPage() {
  const { t } = useTranslation();
  const toc = [
    { id: 'controller', label: t('privacy.toc.controller') },
    { id: 'what',       label: t('privacy.toc.what') },
    { id: 'why',        label: t('privacy.toc.why') },
    { id: 'sharing',    label: t('privacy.toc.sharing') },
    { id: 'transfers',  label: t('privacy.toc.transfers') },
    { id: 'retention',  label: t('privacy.toc.retention') },
    { id: 'rights',     label: t('privacy.toc.rights') },
    { id: 'children',   label: t('privacy.toc.children') },
    { id: 'changes',    label: t('privacy.toc.changes') },
    { id: 'contact',    label: t('privacy.toc.contact') },
  ];
  return (
    <LegalShell
      eyebrow={t('privacy.eyebrow')}
      title={t('privacy.title')}
      subtitle={t('privacy.subtitle')}
      lastUpdated={LAST_UPDATED}
      toc={toc}
    >
      <DocSection id="controller" title={t('privacy.controller.title')}>
        <P>{t('privacy.controller.p1')}</P>
        <P>{t('privacy.controller.p2')}</P>
      </DocSection>

      <DocSection id="what" title={t('privacy.what.title')}>
        <P>{t('privacy.what.p1')}</P>
        <UL>
          <li><Strong>{t('privacy.what.account_title')}</Strong> — {t('privacy.what.account_body')}</li>
          <li><Strong>{t('privacy.what.employee_title')}</Strong> — {t('privacy.what.employee_body')}</li>
          <li><Strong>{t('privacy.what.pii_title')}</Strong> — {t('privacy.what.pii_body')}</li>
          <li><Strong>{t('privacy.what.usage_title')}</Strong> — {t('privacy.what.usage_body')}</li>
        </UL>
      </DocSection>

      <DocSection id="why" title={t('privacy.why.title')}>
        <P>{t('privacy.why.p1')}</P>
        <UL>
          <li>{t('privacy.why.l1')}</li>
          <li>{t('privacy.why.l2')}</li>
          <li>{t('privacy.why.l3')}</li>
          <li>{t('privacy.why.l4')}</li>
        </UL>
      </DocSection>

      <DocSection id="sharing" title={t('privacy.sharing.title')}>
        <P>{t('privacy.sharing.p1')}</P>
        <P>{t('privacy.sharing.p2')}</P>
      </DocSection>

      <DocSection id="transfers" title={t('privacy.transfers.title')}>
        <P>{t('privacy.transfers.p1')}</P>
      </DocSection>

      <DocSection id="retention" title={t('privacy.retention.title')}>
        <P>{t('privacy.retention.p1')}</P>
        <UL>
          <li>{t('privacy.retention.l1')}</li>
          <li>{t('privacy.retention.l2')}</li>
          <li>{t('privacy.retention.l3')}</li>
        </UL>
      </DocSection>

      <DocSection id="rights" title={t('privacy.rights.title')}>
        <P>{t('privacy.rights.p1')}</P>
        <UL>
          <li>{t('privacy.rights.l1')}</li>
          <li>{t('privacy.rights.l2')}</li>
          <li>{t('privacy.rights.l3')}</li>
          <li>{t('privacy.rights.l4')}</li>
          <li>{t('privacy.rights.l5')}</li>
        </UL>
        <P>{t('privacy.rights.p2')}</P>
      </DocSection>

      <DocSection id="children" title={t('privacy.children.title')}>
        <P>{t('privacy.children.p1')}</P>
      </DocSection>

      <DocSection id="changes" title={t('privacy.changes.title')}>
        <P>{t('privacy.changes.p1')}</P>
      </DocSection>

      <DocSection id="contact" title={t('privacy.contact.title')}>
        <P>{t('privacy.contact.p1')} <Code>privacy@keenez.com</Code>.</P>
      </DocSection>
    </LegalShell>
  );
}

/* ────────────────────────────────────────────────────────────────
   Cookies
   ──────────────────────────────────────────────────────────────── */
export function CookiesPage() {
  const { t } = useTranslation();
  return (
    <LegalShell
      eyebrow={t('cookies.eyebrow')}
      title={t('cookies.title')}
      subtitle={t('cookies.subtitle')}
      lastUpdated={LAST_UPDATED}
    >
      <DocSection id="overview" title={t('cookies.overview.title')}>
        <P>{t('cookies.overview.p1')}</P>
      </DocSection>

      <DocSection id="categories" title={t('cookies.categories.title')}>
        <UL>
          <li>
            <Strong>{t('cookies.essential.title')}</Strong> — {t('cookies.essential.body')}
            <span className="block text-xs text-muted-foreground mt-0.5">
              {t('cookies.essential.examples')}: <Code>keenez_session</Code>, <Code>keenez.shell.collapsed</Code>
            </span>
          </li>
          <li>
            <Strong>{t('cookies.functional.title')}</Strong> — {t('cookies.functional.body')}
          </li>
          <li>
            <Strong>{t('cookies.analytics.title')}</Strong> — {t('cookies.analytics.body')}
          </li>
        </UL>
      </DocSection>

      <DocSection id="opt-out" title={t('cookies.optOut.title')}>
        <P>{t('cookies.optOut.p1')}</P>
      </DocSection>
    </LegalShell>
  );
}

/* ────────────────────────────────────────────────────────────────
   Data Processing Agreement (summary)
   ──────────────────────────────────────────────────────────────── */
export function DpaPage() {
  const { t } = useTranslation();
  return (
    <LegalShell
      eyebrow={t('dpa.eyebrow')}
      title={t('dpa.title')}
      subtitle={t('dpa.subtitle')}
      lastUpdated={LAST_UPDATED}
    >
      <DocSection id="roles" title={t('dpa.roles.title')}>
        <P>{t('dpa.roles.p1')}</P>
      </DocSection>

      <DocSection id="scope" title={t('dpa.scope.title')}>
        <P>{t('dpa.scope.p1')}</P>
      </DocSection>

      <DocSection id="security" title={t('dpa.security.title')}>
        <P>{t('dpa.security.p1')}</P>
      </DocSection>

      <DocSection id="subprocessors" title={t('dpa.subprocessors.title')}>
        <P>{t('dpa.subprocessors.p1')}</P>
      </DocSection>

      <DocSection id="transfers" title={t('dpa.transfers.title')}>
        <P>{t('dpa.transfers.p1')}</P>
      </DocSection>

      <DocSection id="rights" title={t('dpa.rights.title')}>
        <P>{t('dpa.rights.p1')}</P>
      </DocSection>

      <DocSection id="contact" title={t('dpa.contact.title')}>
        <P>{t('dpa.contact.p1')} <Code>dpa@keenez.com</Code>.</P>
      </DocSection>
    </LegalShell>
  );
}

/* ────────────────────────────────────────────────────────────────
   Sub-processors
   ──────────────────────────────────────────────────────────────── */
export function SubprocessorsPage() {
  const { t } = useTranslation();
  const subprocessors = [
    { name: 'Hetzner Online GmbH',  purpose: t('subp.purpose.hosting'),    location: 'Germany / Finland (EU)', dpa: 'https://www.hetzner.com/legal/dpa' },
    { name: 'Cloudflare, Inc.',     purpose: t('subp.purpose.cdn'),        location: 'Global edge',            dpa: 'https://www.cloudflare.com/cloudflare-customer-dpa/' },
    { name: 'Cloudflare R2',        purpose: t('subp.purpose.storage'),    location: 'EU jurisdiction',        dpa: 'https://www.cloudflare.com/cloudflare-customer-dpa/' },
    { name: 'OpenRouter, Inc.',     purpose: t('subp.purpose.ai'),         location: 'United States',          dpa: 'https://openrouter.ai/privacy' },
    { name: 'Qdrant Solutions GmbH', purpose: t('subp.purpose.vector'),    location: 'European Union',         dpa: 'https://qdrant.tech/legal/' },
    { name: 'Stripe, Inc.',         purpose: t('subp.purpose.billing'),    location: 'United States / EU',     dpa: 'https://stripe.com/dpa/legal' },
    { name: 'Functional Software, Inc. (Sentry)', purpose: t('subp.purpose.monitoring'), location: 'United States', dpa: 'https://sentry.io/legal/dpa/' },
    { name: 'Transactional email (SMTP)', purpose: t('subp.purpose.email'), location: t('subp.location.perDeployment'), dpa: 'Per your chosen email provider' },
    { name: 'Google LLC',           purpose: t('subp.purpose.oauth'),      location: 'United States',          dpa: 'https://policies.google.com/privacy' },
    { name: 'Apple Inc.',           purpose: t('subp.purpose.oauth'),      location: 'United States',          dpa: 'https://www.apple.com/legal/privacy/' },
  ];
  return (
    <LegalShell
      eyebrow={t('subp.eyebrow')}
      title={t('subp.title')}
      subtitle={t('subp.subtitle')}
      lastUpdated={LAST_UPDATED}
    >
      <div className="overflow-x-auto rounded-2xl border border-border bg-card mt-6">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/40 text-start">
            <tr>
              <th className="px-4 py-3 font-semibold">{t('subp.col.name')}</th>
              <th className="px-4 py-3 font-semibold">{t('subp.col.purpose')}</th>
              <th className="px-4 py-3 font-semibold">{t('subp.col.location')}</th>
              <th className="px-4 py-3 font-semibold">{t('subp.col.dpa')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {subprocessors.map((s) => (
              <tr key={s.name} className="hover:bg-muted/20 transition">
                <td className="px-4 py-3 font-medium">{s.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{s.purpose}</td>
                <td className="px-4 py-3 text-muted-foreground">{s.location}</td>
                <td className="px-4 py-3">
                  <a href={s.dpa} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                    {t('subp.viewDpa')} →
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <P>{t('subp.changes')}</P>
    </LegalShell>
  );
}

/* ────────────────────────────────────────────────────────────────
   Acceptable Use Policy
   ──────────────────────────────────────────────────────────────── */
export function AupPage() {
  const { t } = useTranslation();
  return (
    <LegalShell
      eyebrow={t('aup.eyebrow')}
      title={t('aup.title')}
      subtitle={t('aup.subtitle')}
      lastUpdated={LAST_UPDATED}
    >
      <DocSection id="prohibited" title={t('aup.prohibited.title')}>
        <P>{t('aup.prohibited.p1')}</P>
        <UL>
          <li>{t('aup.prohibited.l1')}</li>
          <li>{t('aup.prohibited.l2')}</li>
          <li>{t('aup.prohibited.l3')}</li>
          <li>{t('aup.prohibited.l4')}</li>
          <li>{t('aup.prohibited.l5')}</li>
          <li>{t('aup.prohibited.l6')}</li>
        </UL>
      </DocSection>

      <DocSection id="enforcement" title={t('aup.enforcement.title')}>
        <P>{t('aup.enforcement.p1')}</P>
      </DocSection>

      <DocSection id="report" title={t('aup.report.title')}>
        <P>{t('aup.report.p1')} <Code>abuse@keenez.com</Code>.</P>
      </DocSection>
    </LegalShell>
  );
}
